import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Typography
} from '@mui/material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { IOptimizationJob } from 'services/routeOptimizationService';
import { JobsTableContainer, LoadingCell, StatusChip } from './styles';

interface JobsTableProps {
  jobs: IOptimizationJob[];
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'otimizando':
      return 'warning';
    case 'reordenando':
      return 'info';
    case 'completo':
      return 'success';
    case 'cancelado':
      return 'error';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'otimizando':
      return 'Otimizando';
    case 'reordenando':
      return 'Reordenando';
    case 'completo':
      return 'Completo';
    case 'cancelado':
      return 'Cancelado';
    default:
      return status;
  }
};

const isJobInProgress = (status: string) => {
  return status === 'otimizando' || status === 'reordenando';
};

const formatDuration = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

const formatDate = (dateString: string) => {
  try {
    return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: ptBR });
  } catch {
    return 'Data inválida';
  }
};

export const JobsTable = ({ jobs }: JobsTableProps) => {
  return (
    <JobsTableContainer>
      <TableContainer component={Paper} elevation={0}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Criado por</TableCell>
              <TableCell>Data de Criação</TableCell>
              <TableCell>Data de Execução</TableCell>
              <TableCell>Duração</TableCell>
              <TableCell>Finalizado em</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <TableRow key={job.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    #{job.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <StatusChip
                      label={getStatusLabel(job.status)}
                      color={getStatusColor(job.status)}
                      size="small"
                      variant="filled"
                    />
                    {isJobInProgress(job.status) && (
                      <CircularProgress size={16} thickness={4} />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {job.createdByName || 'Sistema'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(job.createdAt)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(job.executionDate)}
                  </Typography>
                </TableCell>
                <LoadingCell isLoading={isJobInProgress(job.status)}>
                  <Typography variant="body2">
                    {job.durationSeconds > 0 ? formatDuration(job.durationSeconds) : '-'}
                  </Typography>
                </LoadingCell>
                <TableCell>
                  <Typography variant="body2">
                    {job.terminatedAt ? formatDate(job.terminatedAt) : '-'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </JobsTableContainer>
  );
};
